import {
  DatePicker,
  Field,
  FieldError,
  FieldLabel,
  Label,
  Search,
} from "@/components/ui";
import type { Immunization } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { SubmitCancelButtons } from "../../components/SubmitCancelButtons";
import { useVaccineCodeSearch } from "../network";
import { immunizationFormSchema } from "../schemas";

interface ImmunizationFormProps {
  immunization?: Immunization;
  onSubmit: (data: z.infer<typeof immunizationFormSchema>) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export const ImmunizationForm = ({
  onSubmit,
  onCancel,
  isSubmitting,
}: ImmunizationFormProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { data: vaccineCodes, isLoading: isLoadingVaccineCodes } =
    useVaccineCodeSearch(searchTerm);

  const form = useForm<z.infer<typeof immunizationFormSchema>>({
    resolver: zodResolver(immunizationFormSchema),
    defaultValues: {
      dateAdministered: undefined,
      vaccineCode: undefined,
      vaccineDisplay: undefined,
      vaccineSystem: undefined,
    },
  });

  return (
    <div>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-2 px-3 py-2"
      >
        <Label>Add Immunization</Label>
        <div className="flex flex-row gap-2">
          <Controller
            control={form.control}
            name="dateAdministered"
            render={({ field, fieldState }) => (
              <Field className="flex-1">
                <DatePicker
                  autoFocus
                  label="Date Administered"
                  date={field.value}
                  setDate={field.onChange}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="vaccineCode"
            render={({ fieldState }) => (
              <Field className="flex-1">
                <FieldLabel htmlFor="vaccineCode">Vaccine</FieldLabel>
                <Search
                  placeholder="Search for a vaccine"
                  options={
                    vaccineCodes?.results.map((code) => ({
                      label: code.display,
                      value: code.code,
                      metadata: {
                        system: code.system,
                      },
                    })) || []
                  }
                  onInputChange={(input) => {
                    setSearchTerm(input);
                  }}
                  onItemSelected={(item) => {
                    form.setValue("vaccineCode", item.value as string);
                    form.setValue("vaccineDisplay", item.label);
                    form.setValue(
                      "vaccineSystem",
                      (item.metadata as { system?: string })?.system || "",
                    );
                  }}
                  isLoading={isLoadingVaccineCodes}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <SubmitCancelButtons isSaving={isSubmitting} onCancel={onCancel} />
      </form>
    </div>
  );
};
